import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, X } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { cn } from '@cotera/client/v0/lib/utils';
import { useContentScrollRef } from '../../shell/content-scroll-context';
import {
  ResourceListColgroup,
  ResourceListColumnHeaders,
} from './resource-list-view-column-headers';
import { ResourceListEmptyState } from './resource-list-view-empty';
import { ResourceListErrorState } from './resource-list-view-error';
import { RESOURCE_LIST_SKELETON_CLASS } from './resource-list-view-constants';
import {
  ResourceGridLoadingSkeleton,
  ResourceListLoadingSkeleton,
} from './resource-list-view-loading';
import { ResourceListToolbar } from './resource-list-view-toolbar';
import type {
  Column,
  FilterOption,
  ItemAction,
  SortOption,
} from './resource-list-view-types';
import { VirtualListItem } from './resource-list-view-virtual-item';
import { VirtualGridCard } from './resource-list-view-card';
import { useResourceListDrag } from './use-resource-list-drag';

export type { SortOption, FilterOption, Column, ItemAction };

/** Floating bottom-center pill with the current selection count + bulk actions. */
function SelectionActionBar({
  count,
  actions,
  onClear,
}: {
  count: number;
  actions: ItemAction[];
  onClear: () => void;
}) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto flex items-center gap-1 rounded-full border border-border bg-popover py-1.5 pl-3 pr-1.5 shadow-lg"
      >
        <span className="whitespace-nowrap text-sm font-medium text-foreground tabular-nums">
          {count} selected
        </span>
        <div className="mx-1 h-5 w-px bg-border" />
        {actions.map((action) =>
          action.onClick !== undefined ? (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors',
                action.variant === 'destructive'
                  ? 'text-destructive hover:bg-destructive/10'
                  : 'text-foreground hover:bg-secondary'
              )}
            >
              {/* Same glyph the row's own menu draws for this action, so the
                  bulk bar and the single-row menu read as one vocabulary. The
                  colour comes from the button, not a per-icon class. */}
              {action.icon !== undefined ? <Icon icon={action.icon} /> : null}
              {action.label}
            </button>
          ) : null
        )}
        <div className="mx-1 h-5 w-px bg-border" />
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Wrap secondary list columns (folder, scope, dates, etc.) so they sit on one row
 * below the primary column in a narrow list; once the list itself is wide enough
 * for columns they unwrap into the row via `display: contents`.
 *
 * Keyed to the list's own width (`@list-cols/list:`), not the window's. As a
 * viewport query this measured the wrong box entirely: a list in the chat's
 * artifact panel or the agent editor's Chats tab is far narrower than the window
 * it sits in, so it kept unwrapping into columns that had no room, and the name
 * column — the only cell that isn't `shrink-0` — absorbed the whole shortfall.
 */
export function ResourceListSecondaryCells({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'flex flex-row flex-wrap gap-x-3 gap-y-1.5 w-full min-w-0 justify-start @list-cols/list:contents',
        // One shared centre line for every cell on the stacked meta line.
        //
        // The cells are not the same height and cannot be: a creator or a
        // grantee cell carries a 20px avatar, while a date, a duration or a
        // scope word is an 11px line box — 16.5px at the app's inherited
        // `line-height: 1.5`. Top-aligned, each cell then centred its text
        // against a different height, so the cells without an avatar sat about
        // 2px off the ones with, and the date at the end of the row read as
        // dropped below the names beside it.
        //
        // Centring the line instead lets the short cells centre on the tall
        // one, whatever the tallest turns out to be — which is what keeps this
        // right for the run list's mixed 13px/11px cells too. Stacked only in
        // effect: column mode is `display: contents`, where the row above does
        // the aligning.
        'items-center',
        // Stacked only: drop cells with nothing in them, so the meta line starts
        // at the same left edge on every row.
        //
        // A cell whose value doesn't resolve renders as an empty div — no
        // creator on a type-group folder, no scope on an item that has none —
        // and in column mode that empty box *is* the column, so it has to stay.
        // Wrapped onto one line it is instead 12px of `gap-x-3` holding a space
        // for nothing, which indented the date of any row missing a value and
        // left the column of values looking ragged.
        '@max-list-cols/list:[&>*:empty]:hidden'
      )}
    >
      {children}
    </div>
  );
}

type ResourceListViewProps<T> = {
  title: string;
  /** When true, show a skeleton instead of the title (e.g. while loading folder name). */
  titleLoading?: boolean;
  /**
   * Rendered inline at the end of the page heading, for a control that belongs
   * to the thing the title names — the favorites star on a folder page.
   *
   * Inside `Text.Title`'s children rather than beside it, so it flows after the
   * last word on the title's own line. As a sibling it would centre against the
   * whole two-line block (title plus count) and read as floating between them.
   */
  titleAdornment?: React.ReactNode;
  /**
   * Drops the title and leaves only the count line, for hosts that already name
   * the list right above it — the agent editor's "Chats" tab sits under a tab
   * labelled "Chats", so the serif heading restated it at 36px.
   *
   * The count is restyled rather than just orphaned: as a `Text.Title` subtitle
   * it renders `text-muted-foreground/60 font-light italic`, which reads as a
   * caption hanging off a heading. With nothing above it, it has to stand on its
   * own.
   */
  hideTitle?: boolean;
  /**
   * Replaces the default `"{count} {noun}s"` subtitle under the title. Use when the
   * count is misleading for the view (e.g. a group-by view where the visible rows
   * aren't the counted noun).
   */
  subtitleOverride?: string;
  noun: string;
  items: T[];
  /**
   * Total number of items across all pages, for the count subtitle. Use when the
   * list is paginated and `items` only holds the loaded subset; falls back to
   * `items.length` when omitted.
   */
  totalCount?: number;
  /**
   * `error` renders a retryable failure state. Without it a rejected fetch is
   * indistinguishable from a slow one, because the backing watchable keeps its
   * initial `null` and the list sits on its skeleton forever.
   */
  status: 'loading' | 'ready' | 'error';
  /** Message for the `error` status. */
  errorText?: string;
  /** Retry handler offered alongside `errorText`. */
  onRetry?: () => void;
  /**
   * A refresh is in flight over already-rendered rows (e.g. switching filters).
   * Dims the list and marks it `aria-busy` so the stale rows visibly read as
   * stale instead of silently swapping when the new page lands.
   */
  isRefetching?: boolean;
  filterFn: (item: T, query: string) => boolean;
  /** Return <td> cells for the text content. The leading icon and <tr> wrapper are managed by ResourceListView. */
  renderItem: (item: T) => React.ReactNode;
  /**
   * Per-item row chrome. `custom` hides the shared icon/kebab/borders so the
   * row can render a full card (e.g. Library workflow trigger accordion).
   */
  itemChrome?: (item: T) => 'default' | 'custom';
  /** Leading icon component for each row (e.g. ChatBubbleLeftIcon). Styled internally. On hover, swaps to the actions menu when itemActions is provided. Ignored when renderIcon is provided. */
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Optional custom renderer for the first (icon) cell per row. When provided, icon and per-row actions dropdown are not used for that cell. */
  renderIcon?: (item: T) => React.ReactNode;
  keyFn: (item: T) => string;
  onClickItem?: (item: T) => void;
  /**
   * The page a row opens, as a real URL. When set, the row's content renders as
   * an anchor so ⌘/ctrl-click, middle-click and the browser's "Open link in new
   * tab" open it in a new tab; a plain click still goes through `onClickItem`.
   */
  itemHref?: (item: T) => string | undefined;
  /** Called when focused selection changes. Emits one or more focused items. */
  onFocus?: (items: T[]) => void;
  itemActions?: (item: T) => ItemAction[];
  /**
   * Bulk actions for the current selection. When provided, a floating action
   * bar appears as soon as 1+ rows are selected, showing "{n} selected" + these
   * actions + a clear button (so users don't have to discover the command
   * palette). The actions operate on the selection the page already tracks.
   */
  selectionActions?: (items: T[]) => ItemAction[];
  /**
   * When true, every row shows a persistent selection checkbox (instead of only
   * once a selection exists / Shift is held) and a "select all" checkbox appears
   * in the column header. The checkbox sits in its own leading column, so the
   * type icon and per-row actions menu are preserved. Use for lists whose primary
   * job includes bulk operations (e.g. the Library).
   */
  alwaysShowSelection?: boolean;
  /**
   * Gives the per-row `⋯` menu its own trailing column instead of letting it
   * overlay (and fade out) the leading type icon on hover.
   *
   * Defaults to `alwaysShowSelection`, which is how the Library got it — but the
   * two are unrelated concerns, and a list that wants Library-style row actions
   * shouldn't have to take bulk-selection checkboxes to get them. Automations
   * reaches the same result the long way round, rendering its own trailing slot
   * inside a `custom`-chrome row.
   */
  trailingActionsColumn?: boolean;
  /**
   * Enables drag-and-drop of rows onto other rows (e.g. drag library items onto
   * a folder to move them). Called with the drop-target row and the dragged
   * rows. Dragging a row inside the current selection carries the whole
   * selection; dragging any other row carries just that row. Drag-and-drop is
   * only active when this is provided.
   */
  onDropOnItem?: (target: T, draggedItems: T[]) => void;
  /**
   * Whether a row can receive a drop (highlighted + accepts the drop). Defaults
   * to no rows. Only consulted when `onDropOnItem` is set.
   */
  canDropOnItem?: (item: T) => boolean;
  /**
   * Per-row gate on dragging (e.g. virtual/synthetic rows that aren't real
   * resources). Defaults to allowing every row. Only consulted when
   * `onDropOnItem` is set.
   */
  canDragItem?: (item: T) => boolean;
  /**
   * Custom drag image rendered under the cursor while dragging (e.g. a
   * Google-Drive-style card showing the grabbed item's icon + name and a count
   * badge). Receives the dragged rows. Falls back to the browser's default row
   * snapshot when omitted. Only used when `onDropOnItem` is set.
   */
  renderDragImage?: (draggedItems: T[]) => React.ReactNode;
  /** Called when the row action menu (ellipsis) is opened. Use to set focused/selected item before actions run. */
  onOpenActionMenu?: (item: T) => void;
  emptyIcon: React.ReactNode;
  emptyText: string;
  noMatchText: string;
  /**
   * Resets every filter narrowing the list. Surfaced in the empty state so a
   * filtered-to-nothing list is never a dead end.
   */
  onClearFilters?: () => void;
  /** Whether a filter beyond the search box is currently narrowing the list. */
  filtersActive?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    /**
     * When provided, the `+` button opens a menu of these options instead of
     * firing `onClick` directly (e.g. "New trigger" / "New workflow").
     */
    menuItems?: {
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
    }[];
  };
  searchPlaceholder: string;
  sortOptions?: SortOption<T>[];
  defaultSort?: string;
  filterOptions?: FilterOption[];
  columns: Column[];
  /** When true, list supports infinite scroll; when sentinel is near bottom, onLoadMore or loadPage is called. */
  hasMore?: boolean;
  /**
   * Called when user scrolls near bottom and hasMore is true.
   * Parent must update URL (e.g. setSearchParams with page/limit) or other persisted state
   * so that refetches use the same range and scroll position is preserved.
   */
  onLoadMore?: () => void | Promise<void>;
  /**
   * Alternative to onLoadMore: called with (page, take) when user scrolls near bottom.
   * When provided, page and take must be passed so the view can call loadPage(page + 1, take).
   */
  loadPage?: (page: number, take: number) => void | Promise<void>;
  /** Current 1-based page (required when loadPage is used). */
  page?: number;
  /** Current page size (required when loadPage is used). */
  take?: number;
  /** When true, do not trigger onLoadMore/loadPage (e.g. while parent is fetching). */
  isLoadingMore?: boolean;
  /** When provided, search is controlled and synced to URL by the parent. */
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  /** When provided, sort is controlled and synced to URL by the parent. */
  activeSort?: string;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  /** Extra controls to render in the toolbar, to the right of filter/search and left of primary action. */
  toolbarExtras?: React.ReactNode;
  /** Extra content rendered in the sticky header, between the title/toolbar row and the column headers (e.g. a stats row). */
  headerExtras?: React.ReactNode;
  /** When provided, replaces the list body (kept header/toolbar). Use for alternate views like calendars. */
  contentOverride?: React.ReactNode;
  /**
   * `page` — standalone list page with generous top padding.
   * `embedded` — artifact panel / nested tab; tighter top spacing.
   * `panel` — bottom drawer / collapsible strip; no page title (chrome owns the label).
   */
  layout?: 'page' | 'embedded' | 'panel';
  /**
   * `compact` trims the title and top padding. The default header spends ~250px
   * across four stacked bands before the first row, on pages whose title often
   * just restates the nav item the user clicked. Opt-in per page so the eight
   * existing callers keep their current look until each is reviewed.
   */
  headerSize?: 'default' | 'compact';
  /**
   * Replaces the root container's default padding (`px-8`). Use when the host
   * page owns the page gutters itself, so the two don't stack.
   */
  containerClassName?: string;
  /**
   * `grid` renders `renderCard` in a responsive card grid instead of rows.
   *
   * A first-class mode rather than a `contentOverride`, because the override
   * replaces the whole body — a grid built that way would forfeit selection,
   * drag-and-drop, keyboard navigation and infinite scroll, all of which are
   * index-based and work unchanged here.
   */
  layoutMode?: 'list' | 'grid';
  /** Card body for `layoutMode="grid"`. Falls back to rows when omitted. */
  renderCard?: (item: T) => React.ReactNode;
  /**
   * Tree position for a row (depth, whether it has children, whether it's
   * expanded). Supplying this switches the list to `treegrid` and gives
   * expandable rows a disclosure control, indentation, and ArrowLeft/ArrowRight
   * handling. Omit for a flat list.
   */
  itemTreeMeta?: (item: T) => ResourceListTreeMeta | undefined;
  /** Called when a row's disclosure control (or ArrowLeft/Right) toggles it. */
  onToggleExpand?: (item: T) => void;
};

/** Tree position of a row. `depth` is 0-based; `aria-level` is 1-based. */
export type ResourceListTreeMeta = {
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  isLoadingChildren?: boolean;
};

/**
 * Card metrics for grid mode. Height feeds the row virtualizer's estimate.
 *
 * Sized to fit a two-line title plus a two-line body (e.g. an agent's summary
 * of what it does) above the owner/date footer, without the tallest card
 * clipping — every card in a row shares this height.
 */
const GRID_CARD_HEIGHT_PX = 146;
const GRID_GAP_PX = 12;

/** Cards per row at a full-width desktop pane; narrower viewports step down. */
const GRID_TARGET_COLUMNS = 4;

/**
 * Breakpoints stepping down from {@link GRID_TARGET_COLUMNS}. Deliberately a
 * fixed ladder rather than a min-width fit: a fit-based count makes card size
 * drift with the pane, so the same library looked different at every window
 * size. Change `GRID_TARGET_COLUMNS` to retune the whole ladder.
 */
const GRID_COLUMN_BREAKPOINTS: { minWidth: number; columns: number }[] = [
  { minWidth: 1100, columns: GRID_TARGET_COLUMNS },
  { minWidth: 820, columns: 3 },
  { minWidth: 520, columns: 2 },
];

/**
 * How many cards go in a row at a given container width. Exported for tests —
 * the virtualizer chunks items by this, so an off-by-one drops or duplicates a
 * whole row of cards.
 *
 * A width of 0 means "not measured yet"; it returns the target rather than 1 so
 * a first paint before the ResizeObserver reports can't flash a single
 * full-width card.
 */
export function gridColumnsForWidth(width: number): number {
  if (width <= 0) {
    return GRID_TARGET_COLUMNS;
  }
  const match = GRID_COLUMN_BREAKPOINTS.find((bp) => width >= bp.minWidth);
  return match?.columns ?? 1;
}

type SelectionState = {
  focusedIndex: number | null;
  selectionAnchorIndex: number | null;
  selectedIndices: number[];
};

/**
 * The heading slot under `hideTitle` — the count alone, standing where a title
 * would be, for hosts that already name the list right above it.
 *
 * Its own component rather than a third arm of the heading's ternary: with the
 * loading and empty cases it needs three outcomes of its own, and nested inside
 * the ternary that picks between the panel / hidden / full headings it made a
 * four-deep conditional that Prettier could only format by orphaning the
 * comments above a bare `null`.
 */
const ListCountHeading: React.FC<{
  titleLoading: boolean;
  /** See `nothingToCount` in {@link ResourceListView}. */
  nothingToCount: boolean;
  subtitle: React.ReactNode;
}> = ({ titleLoading, nothingToCount, subtitle }) => {
  if (titleLoading) {
    return (
      <Skeleton
        className={cn(RESOURCE_LIST_SKELETON_CLASS, 'h-4 w-20 rounded')}
      />
    );
  }
  // "0 chats" above "No chats with this agent yet" says the same thing twice,
  // and the weaker of the two first — the empty state carries it alone.
  if (nothingToCount) {
    return null;
  }
  // Same role as the `panel` layout's count, one step up in size because
  // `hideTitle` hosts are wider than a chat artifact pane. Not `font-medium`: it
  // stands in for a title's position, not for a title.
  return (
    <span className="text-sm text-muted-foreground tabular-nums">
      {subtitle}
    </span>
  );
};

export function ResourceListView<T>({
  title,
  titleLoading = false,
  titleAdornment,
  hideTitle = false,
  subtitleOverride,
  noun,
  items,
  totalCount,
  status,
  errorText = 'Something went wrong loading this list.',
  onRetry,
  isRefetching = false,
  filterFn,
  renderItem,
  itemChrome,
  icon: Icon,
  renderIcon,
  keyFn,
  onClickItem,
  itemHref,
  onFocus: onFocusItems,
  itemActions,
  selectionActions,
  alwaysShowSelection = false,
  trailingActionsColumn,
  onDropOnItem,
  canDropOnItem,
  canDragItem,
  renderDragImage,
  onOpenActionMenu,
  emptyIcon,
  emptyText,
  noMatchText,
  onClearFilters,
  filtersActive = false,
  primaryAction,
  searchPlaceholder,
  sortOptions,
  defaultSort,
  filterOptions,
  columns,
  hasMore = false,
  onLoadMore,
  loadPage,
  page,
  take,
  isLoadingMore = false,
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
  activeSort: controlledActiveSort,
  sortDirection: controlledSortDirection,
  onSortChange,
  toolbarExtras,
  headerExtras,
  contentOverride,
  layout = 'page',
  headerSize = 'default',
  containerClassName,
  layoutMode = 'list',
  renderCard,
  itemTreeMeta,
  onToggleExpand,
}: ResourceListViewProps<T>): React.ReactElement {
  const contentScrollRef = useContentScrollRef();
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [internalActiveSort, setInternalActiveSort] = useState<
    string | undefined
  >(defaultSort);
  const [internalSortDirection, setInternalSortDirection] = useState<
    'asc' | 'desc'
  >('asc');

  const isSearchControlled = controlledSearchQuery !== undefined;
  const isSortControlled =
    controlledActiveSort !== undefined && controlledSortDirection !== undefined;
  const searchQuery = isSearchControlled
    ? controlledSearchQuery
    : internalSearchQuery;
  const setSearchQuery = useCallback(
    (value: string) => {
      if (onSearchQueryChange !== undefined) {
        onSearchQueryChange(value);
      } else {
        setInternalSearchQuery(value);
      }
    },
    [onSearchQueryChange]
  );
  const activeSort = isSortControlled
    ? controlledActiveSort
    : internalActiveSort;
  const sortDirection = isSortControlled
    ? controlledSortDirection ?? 'asc'
    : internalSortDirection;

  const [selectionState, setSelectionState] = useState<SelectionState>({
    focusedIndex: null,
    selectionAnchorIndex: null,
    selectedIndices: [],
  });
  const { focusedIndex, selectionAnchorIndex, selectedIndices } =
    selectionState;
  // Drag-and-drop (opt-in via onDropOnItem): the row indices currently being
  // dragged, plus the row hovered as a drop target (for highlight).
  const dndEnabled = onDropOnItem !== undefined;
  // Whether the listbox currently holds DOM focus — gates the visible keyboard
  // cursor so a row highlighted by arrow-keys doesn't linger like a stuck hover
  // once focus leaves the list.
  const [listHasFocus, setListHasFocus] = useState(false);
  // Only auto-scroll the focused row into view for *keyboard* navigation. On a
  // mouse click the row is already visible, and scrolling it "into view" past
  // the sticky header yanked the list up (hiding rows under the header).
  const focusFromKeyboardRef = useRef(false);
  const ROW_HEIGHT = 60;
  const OVERSCAN = 100;
  // Embedded/panel lists are placed inside a host layout that already bounds
  // their height (e.g. the agent editor chats tab, or the workflow Runs
  // bottom strip). Deferring to an ancestor ContentScrollProvider would never
  // scroll, since the scroll container is separated from the list by those
  // bounded wrappers. So those layouts always own their own scrolling.
  const useExternalScroll =
    contentScrollRef !== null && layout !== 'embedded' && layout !== 'panel';
  const rowRefs = useRef<(HTMLElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  // Set below: the measured height of the sticky header band, published as a CSS
  // variable so descendants can pin themselves *under* it. The grouped
  // Automations view uses it to stick its trigger headers; the band's height is
  // not a constant (title, chips and column headers all vary), so it has to be
  // measured rather than hardcoded.
  const containerRef = useRef<HTMLDivElement>(null);
  const headerExtrasRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const activeSortOption = useMemo(
    () => sortOptions?.find((s) => s.key === activeSort),
    [sortOptions, activeSort]
  );
  const selectedIndexSet = useMemo(
    () => new Set<number>(selectedIndices),
    [selectedIndices]
  );

  const sortIsServerControlled = onSortChange !== undefined;
  const filteredAndSortedItems = useMemo(() => {
    let result = items;
    if (searchQuery.trim() !== '') {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((item) => filterFn(item, query));
    }
    if (activeSortOption !== undefined && !sortIsServerControlled) {
      const dir = sortDirection === 'asc' ? 1 : -1;
      result = [...result].sort(
        (a, b) => activeSortOption.compareFn(a, b) * dir
      );
    }
    return result;
  }, [
    items,
    searchQuery,
    filterFn,
    activeSortOption,
    sortDirection,
    sortIsServerControlled,
  ]);

  // Identity of the currently displayed dataset. Changes when the list swaps to
  // a different view — a new folder, a type/ownership lens switch, a search, or
  // a sort — even if the row count happens to stay the same. Used to re-arm
  // keyboard focus so navigation survives those transitions (see below).
  const viewSignature = useMemo(() => {
    const first = filteredAndSortedItems[0];
    return `${filteredAndSortedItems.length}:${
      first !== undefined ? keyFn(first) : ''
    }`;
  }, [filteredAndSortedItems, keyFn]);

  // Row keys from the previous render, so the effect below can map an old
  // selection index to where that same row sits now — or discover it is gone.
  const prevKeysRef = useRef<string[]>([]);
  // `keyFn` is frequently an inline arrow (a fresh function each render), so it
  // must not gate the reconciliation effect below or it would re-run — and
  // re-emit — on every render. Read it through a ref and key the effect off the
  // data instead.
  const keyFnRef = useRef(keyFn);
  keyFnRef.current = keyFn;

  // Reconcile the index-based selection against row *identity* whenever the
  // displayed rows change, then emit the reconciled focus to the host.
  //
  // Selection and the keyboard cursor are stored as indices into
  // `filteredAndSortedItems`. When rows are removed out from under a selection —
  // moved into a folder, or archived/deleted — those indices would otherwise
  // keep pointing at whatever rows shifted into the vacated slots, so the
  // selection appeared to jump to unrelated items instead of clearing. Mapping
  // each index back through the row's stable key fixes that: rows that are gone
  // drop out, and survivors follow their rows to their new positions.
  //
  // Reconcile and emit live in one effect on purpose (rather than a reconcile
  // effect plus a separate emit effect): the emit is derived from the freshly
  // reconciled selection, so a just-removed selection can never briefly
  // re-target a neighbouring row on its way to clearing.
  useEffect(() => {
    const prevKeys = prevKeysRef.current;
    const currentKeys = filteredAndSortedItems.map((item) =>
      keyFnRef.current(item)
    );
    prevKeysRef.current = currentKeys;
    const length = currentKeys.length;

    const newIndexByKey = new Map<string, number>();
    currentKeys.forEach((key, index) => {
      if (newIndexByKey.has(key) === false) {
        newIndexByKey.set(key, index);
      }
    });
    const remap = (index: number | null): number | null => {
      if (index === null) {
        return null;
      }
      const key = prevKeys[index];
      if (key === undefined) {
        return null;
      }
      return newIndexByKey.get(key) ?? null;
    };

    const remappedSelected = [
      ...new Set(
        selectedIndices
          .map((index) => remap(index))
          .filter((index): index is number => index !== null)
      ),
    ].sort((a, b) => a - b);

    const hadSelection = selectedIndices.length > 0;
    // A multi-selection whose every row was removed returns all the way to base:
    // no lingering action bar, and no cursor left clamped on a neighbour for the
    // host to re-target. A bare keyboard cursor (no active selection) still
    // clamps below, so arrow-key navigation survives an unrelated list refresh.
    const selectionClearedByRemoval =
      hadSelection === true && remappedSelected.length === 0;

    let next: SelectionState;
    if (length === 0 || selectionClearedByRemoval === true) {
      next = {
        focusedIndex: null,
        selectionAnchorIndex: null,
        selectedIndices: [],
      };
    } else {
      let nextFocusedIndex = remap(focusedIndex);
      if (nextFocusedIndex === null && focusedIndex !== null) {
        nextFocusedIndex = Math.min(focusedIndex, length - 1);
      }
      next = {
        focusedIndex: nextFocusedIndex,
        selectionAnchorIndex: remap(selectionAnchorIndex),
        selectedIndices: remappedSelected,
      };
    }

    const changed =
      next.focusedIndex !== focusedIndex ||
      next.selectionAnchorIndex !== selectionAnchorIndex ||
      next.selectedIndices.length !== selectedIndices.length ||
      next.selectedIndices.some((index, i) => index !== selectedIndices[i]);
    if (changed === true) {
      setSelectionState(next);
    }

    if (onFocusItems === undefined) {
      return;
    }
    const focusedItems = next.selectedIndices
      .map((index) => filteredAndSortedItems[index])
      .filter((item): item is T => item !== undefined);
    if (focusedItems.length > 0) {
      onFocusItems(focusedItems);
      return;
    }
    if (next.focusedIndex !== null) {
      const focusedItem = filteredAndSortedItems[next.focusedIndex];
      if (focusedItem !== undefined) {
        onFocusItems([focusedItem]);
        return;
      }
    }
    // Only announce an empty selection when one was actively cleared by removal —
    // never for a list that merely arrived empty or a bare cursor — so a plain
    // list shrink can't wipe the host's target out from under it.
    if (selectionClearedByRemoval === true) {
      onFocusItems([]);
    }
  }, [
    filteredAndSortedItems,
    focusedIndex,
    selectionAnchorIndex,
    selectedIndices,
    onFocusItems,
  ]);

  // Grid mode virtualizes by *row of cards*, so the virtualizer's unit is a
  // chunk of `gridColumns` items rather than a single item.
  const isGridMode = layoutMode === 'grid' && renderCard !== undefined;
  // Start at the target rather than 1: the very first paint has no measurement
  // yet, and seeding 1 rendered a single full-width card until the observer
  // reported.
  const [gridColumns, setGridColumns] = useState(GRID_TARGET_COLUMNS);
  // The scroll container only mounts once there are rows to show, so this must
  // re-run when the body appears — not just when the mode changes. Depending on
  // `isGridMode` alone left `listRef` null on the first pass (status is still
  // `loading`), so the observer never attached and the count stayed at its seed
  // after any reload or tab switch.
  const hasRenderedBody =
    status === 'ready' && filteredAndSortedItems.length > 0;
  useEffect(() => {
    if (!isGridMode || !hasRenderedBody) {
      return;
    }
    const el = listRef.current;
    if (el === null) {
      return;
    }
    const apply = (): void => {
      // A container mid-transition can measure 0; ignore that rather than
      // collapsing to one column.
      if (el.clientWidth <= 0) {
        return;
      }
      setGridColumns(gridColumnsForWidth(el.clientWidth));
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isGridMode, hasRenderedBody]);

  const virtualCount = isGridMode
    ? Math.ceil(filteredAndSortedItems.length / Math.max(1, gridColumns))
    : filteredAndSortedItems.length;

  // Key measured row heights by stable item id, not by index. Rows are variable
  // height (a title can gain a summary line, and below `md` the secondary cells
  // wrap under it), so the virtualizer's cache must follow the *item*. Without
  // this, re-sorting/filtering to a same-length list leaves index N holding the
  // previous item's cached height — the next row is then positioned on top of it
  // and the two overlap. In grid mode each virtual row is a fixed-height card row
  // (no dynamic measurement), so a per-row-index key is correct there.
  const getItemKey = useCallback(
    (index: number): string | number => {
      if (isGridMode) {
        return `grid-row-${index}`;
      }
      const item = filteredAndSortedItems[index];
      return item !== undefined ? keyFn(item) : index;
    },
    [isGridMode, filteredAndSortedItems, keyFn]
  );
  const rowVirtualizer = useVirtualizer({
    count: virtualCount,
    getScrollElement: () =>
      (useExternalScroll ? contentScrollRef?.current : listRef.current) ?? null,
    estimateSize: () =>
      isGridMode ? GRID_CARD_HEIGHT_PX + GRID_GAP_PX : ROW_HEIGHT,
    overscan: OVERSCAN,
    getItemKey,
  });
  // Stable ref callback: React runs a fresh closure on every render with `null`
  // then the element, which thrashes measureElement's ResizeObserver. Keeping it
  // stable (and reading the row index from `data-index`) avoids that churn.
  const measureRowElement = useCallback(
    (el: HTMLDivElement | null) => {
      if (el !== null) {
        const idx = Number(el.dataset['index']);
        if (!Number.isNaN(idx)) {
          rowRefs.current[idx] = el;
        }
      }
      rowVirtualizer.measureElement(el);
    },
    [rowVirtualizer]
  );
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();

  useEffect(() => {
    rowVirtualizer.measure();
    // `viewSignature` changes on any dataset swap (folder / lens / search / sort),
    // including re-sorts that keep the same row count — those must re-measure so
    // stale heights don't linger and overlap rows.
  }, [viewSignature, rowVirtualizer, gridColumns, isGridMode]);

  useEffect(() => {
    const triggerLoadMore = (): void => {
      if (loadPage !== undefined && page !== undefined && take !== undefined) {
        void loadPage(page + 1, take);
      } else if (onLoadMore !== undefined) {
        void onLoadMore();
      }
    };
    if (
      !hasMore ||
      isLoadingMore ||
      status === 'loading' ||
      items.length === 0 ||
      (loadPage === undefined && onLoadMore === undefined)
    ) {
      return;
    }
    if (loadPage !== undefined && (page === undefined || take === undefined)) {
      return;
    }
    const scrollEl = useExternalScroll
      ? contentScrollRef?.current
      : listRef.current;
    const sentinel = sentinelRef.current;
    if (scrollEl === null || sentinel === null) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting === true) {
          triggerLoadMore();
        }
      },
      { root: scrollEl, threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasMore,
    isLoadingMore,
    status,
    items.length,
    onLoadMore,
    loadPage,
    page,
    take,
    filteredAndSortedItems.length,
    useExternalScroll,
    contentScrollRef,
  ]);

  useEffect(() => {
    if (
      focusFromKeyboardRef.current &&
      focusedIndex !== null &&
      rowRefs.current[focusedIndex] !== undefined
    ) {
      rowRefs.current[focusedIndex]?.scrollIntoView({ block: 'nearest' });
    }
    focusFromKeyboardRef.current = false;
  }, [focusedIndex]);

  useEffect(() => {
    if (status === 'loading' || filteredAndSortedItems.length === 0) {
      return;
    }
    // Give the list keyboard focus when a fresh dataset settles — the first
    // load, and view changes that keep this component mounted (folder
    // navigation, a type/ownership lens switch), all captured by `viewSignature`
    // in the deps. Without this, arriving on the page leaves focus wherever the
    // user clicked to get here (e.g. the "Library" sidebar link), so arrow keys
    // do nothing and there's no non-destructive way in — a row click OPENS the
    // item rather than focusing.
    //
    // The one thing we must NOT do is yank focus off a control *within this
    // component* that the user is actively driving — the search box, or a header
    // chip/menu they're keyboarding through. Focus anywhere else (the sidebar,
    // <body>, or already in the list) is fair game to pull into the list.
    const active = document.activeElement;
    const focusOnOwnHeaderControl =
      active instanceof Node &&
      containerRef.current !== null &&
      containerRef.current.contains(active) &&
      (listRef.current === null || !listRef.current.contains(active));
    if (focusOnOwnHeaderControl) {
      return;
    }
    const t = setTimeout(() => {
      listRef.current?.focus({ preventScroll: true });
    }, 50);
    return () => clearTimeout(t);
  }, [status, viewSignature, filteredAndSortedItems.length]);

  // Move focus onto the header controls (the type chips). Prefers the active
  // roving item (tabindex 0), falling back to the first focusable element.
  const focusHeaderExtras = useCallback((): boolean => {
    const container = headerExtrasRef.current;
    if (container === null) {
      return false;
    }
    const target =
      container.querySelector<HTMLElement>('[tabindex="0"]') ??
      container.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
    if (target === null) {
      return false;
    }
    target.focus();
    return true;
  }, []);

  // From the header controls, ArrowDown drops focus back into the list. (Radix
  // roving groups keep Left/Right for moving across the chips; the ownership
  // Select consumes ArrowDown to open, which `defaultPrevented` respects.)
  const handleHeaderExtrasKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowDown' || e.defaultPrevented) {
        return;
      }
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      focusFromKeyboardRef.current = true;
      listRef.current?.focus({ preventScroll: true });
    },
    []
  );

  // A tree list renders as `treegrid` with disclosure controls; a flat one stays
  // a plain `grid`.
  const isTreeList = itemTreeMeta !== undefined;

  const onOpenSearch = useCallback(() => {
    setSearchOpen(true);
    // The input only mounts once open, so focus on the next frame.
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });
  }, []);

  const handleListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const count = filteredAndSortedItems.length;
      if (count === 0) {
        return;
      }
      // Keyboard-driven focus changes should scroll the row into view.
      focusFromKeyboardRef.current = true;
      const createSelectionRange = (start: number, end: number): number[] => {
        const min = Math.min(start, end);
        const max = Math.max(start, end);
        const range: number[] = [];
        for (let index = min; index <= max; index += 1) {
          range.push(index);
        }
        return range;
      };
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectionState((prev) => {
          const nextFocusedIndex =
            prev.focusedIndex === null
              ? 0
              : Math.min(prev.focusedIndex + 1, count - 1);
          if (e.shiftKey) {
            const anchorIndex =
              prev.selectionAnchorIndex ??
              prev.focusedIndex ??
              nextFocusedIndex;
            return {
              focusedIndex: nextFocusedIndex,
              selectionAnchorIndex: anchorIndex,
              selectedIndices: createSelectionRange(
                anchorIndex,
                nextFocusedIndex
              ),
            };
          }
          // Plain nav moves the cursor but PRESERVES any active selection, so
          // arrow keys don't kick you out of multi-select. Space toggles the
          // focused row; Escape clears the whole selection.
          return { ...prev, focusedIndex: nextFocusedIndex };
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        // At the top of the list, plain ArrowUp escapes upward into the header
        // controls (the type chips), so the toolbar and list read as one
        // keyboard flow. shift+ArrowUp keeps extending the selection instead.
        if (!e.shiftKey && focusedIndex === 0 && focusHeaderExtras()) {
          return;
        }
        setSelectionState((prev) => {
          const nextFocusedIndex =
            prev.focusedIndex === null
              ? count - 1
              : Math.max(prev.focusedIndex - 1, 0);
          if (e.shiftKey) {
            const anchorIndex =
              prev.selectionAnchorIndex ??
              prev.focusedIndex ??
              nextFocusedIndex;
            return {
              focusedIndex: nextFocusedIndex,
              selectionAnchorIndex: anchorIndex,
              selectedIndices: createSelectionRange(
                anchorIndex,
                nextFocusedIndex
              ),
            };
          }
          // Plain nav preserves the selection (see ArrowDown).
          return { ...prev, focusedIndex: nextFocusedIndex };
        });
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        setSelectionState((prev) => {
          if (e.shiftKey) {
            const anchorIndex =
              prev.selectionAnchorIndex ?? prev.focusedIndex ?? 0;
            return {
              focusedIndex: 0,
              selectionAnchorIndex: anchorIndex,
              selectedIndices: createSelectionRange(anchorIndex, 0),
            };
          }
          return { ...prev, focusedIndex: 0 };
        });
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        setSelectionState((prev) => {
          const lastIndex = count - 1;
          if (e.shiftKey) {
            const anchorIndex =
              prev.selectionAnchorIndex ?? prev.focusedIndex ?? lastIndex;
            return {
              focusedIndex: lastIndex,
              selectionAnchorIndex: anchorIndex,
              selectedIndices: createSelectionRange(anchorIndex, lastIndex),
            };
          }
          return { ...prev, focusedIndex: lastIndex };
        });
        return;
      }
      if (e.key === ' ' || e.key === 'Spacebar') {
        // Space toggles the focused row's selection (like clicking its
        // checkbox) so you can build a multi-selection purely with the keyboard.
        if (focusedIndex === null) {
          return;
        }
        e.preventDefault();
        setSelectionState((prev) => {
          if (prev.focusedIndex === null) {
            return prev;
          }
          const idx = prev.focusedIndex;
          const selected = new Set(prev.selectedIndices);
          if (selected.has(idx)) {
            selected.delete(idx);
          } else {
            selected.add(idx);
          }
          const selectedIndices = [...selected].sort((a, b) => a - b);
          return {
            focusedIndex: idx,
            selectionAnchorIndex:
              selectedIndices.length === 0
                ? null
                : prev.selectionAnchorIndex ?? idx,
            selectedIndices,
          };
        });
        return;
      }
      if (
        (e.key === 'ArrowRight' || e.key === 'ArrowLeft') &&
        isTreeList &&
        focusedIndex !== null
      ) {
        // Standard tree keys: Right expands a collapsed row, Left collapses an
        // expanded one. Both are no-ops on rows that can't expand, so they fall
        // through to the browser rather than swallowing horizontal navigation.
        const focusedItem = filteredAndSortedItems[focusedIndex];
        if (focusedItem === undefined) {
          return;
        }
        const meta = itemTreeMeta?.(focusedItem);
        if (meta === undefined || !meta.hasChildren) {
          return;
        }
        const wantsExpanded = e.key === 'ArrowRight';
        if (meta.isExpanded !== wantsExpanded) {
          e.preventDefault();
          onToggleExpand?.(focusedItem);
        }
        return;
      }
      if ((e.key === 'a' || e.key === 'A') && (e.metaKey || e.ctrlKey)) {
        // ⌘A / Ctrl+A selects every loaded row, matching every other list UI.
        // Previously select-all was mouse-only.
        e.preventDefault();
        setSelectionState({
          focusedIndex: focusedIndex ?? 0,
          selectionAnchorIndex: 0,
          selectedIndices: Array.from({ length: count }, (_, i) => i),
        });
        return;
      }
      if (
        e.key === '/' ||
        ((e.key === 'f' || e.key === 'F') && (e.metaKey || e.ctrlKey))
      ) {
        // Jump to the list's own search. ⌘F is intercepted deliberately: the
        // list is virtualized, so the browser's find-in-page can only see the
        // handful of rows currently mounted.
        e.preventDefault();
        onOpenSearch();
        return;
      }
      if (e.key === 'Escape') {
        // Escape is the deliberate way out of multi-select — clear the whole
        // selection but keep the cursor where it is.
        if (selectedIndices.length === 0) {
          return;
        }
        e.preventDefault();
        setSelectionState((prev) => ({
          focusedIndex: prev.focusedIndex,
          selectionAnchorIndex: null,
          selectedIndices: [],
        }));
        return;
      }
      if (e.key === 'Enter' && focusedIndex !== null) {
        const item = filteredAndSortedItems[focusedIndex];
        if (item !== undefined && onClickItem !== undefined) {
          e.preventDefault();
          onClickItem(item);
        }
        return;
      }
    },
    [
      filteredAndSortedItems,
      focusedIndex,
      selectedIndices,
      onClickItem,
      focusHeaderExtras,
      onOpenSearch,
      isTreeList,
      itemTreeMeta,
      onToggleExpand,
    ]
  );

  const handleListFocus = useCallback(() => {
    setListHasFocus(true);
    if (filteredAndSortedItems.length === 0) {
      return;
    }
    setSelectionState((prev) => {
      if (prev.focusedIndex !== null) {
        return prev;
      }
      // Focusing the list positions the keyboard cursor on the first row without
      // *selecting* it (and without setting a selection anchor, so a following
      // shift-click anchors at the clicked row, not row 0).
      return {
        focusedIndex: 0,
        selectionAnchorIndex: null,
        selectedIndices: [],
      };
    });
  }, [filteredAndSortedItems.length]);

  const createSelectionRange = useCallback(
    (start: number, end: number): number[] => {
      const min = Math.min(start, end);
      const max = Math.max(start, end);
      const range: number[] = [];
      for (let index = min; index <= max; index += 1) {
        range.push(index);
      }
      return range;
    },
    []
  );

  const handleToggleSelect = useCallback((index: number) => {
    focusFromKeyboardRef.current = false;
    setSelectionState((prev) => {
      const selected = new Set(prev.selectedIndices);
      if (selected.has(index)) {
        selected.delete(index);
      } else {
        selected.add(index);
      }
      const selectedIndices = [...selected].sort((a, b) => a - b);
      // Unchecking the last box clears the selection (previously it re-added the
      // item, so you could never deselect down to zero).
      if (selectedIndices.length === 0) {
        return {
          focusedIndex: index,
          selectionAnchorIndex: null,
          selectedIndices: [],
        };
      }
      return {
        focusedIndex: index,
        // A plain toggle re-anchors to this row, so a following shift-click
        // ranges from here (standard behavior) rather than from the original
        // anchor — letting you build disjoint ranges (e.g. 1–5, then 7–10).
        selectionAnchorIndex: index,
        selectedIndices,
      };
    });
  }, []);

  // Range-select from the last real selection anchor, or from this row if there
  // is none. It must NOT fall back to the keyboard cursor, or a shift-click on a
  // freshly-focused list would select from row 0 down. Shared by shift-clicking
  // a row and shift-clicking a checkbox.
  const handleRangeSelectTo = useCallback(
    (index: number) => {
      focusFromKeyboardRef.current = false;
      setSelectionState((prev) => {
        const anchorIndex = prev.selectionAnchorIndex ?? index;
        // Union the anchor→index range into the existing selection so shift-click
        // extends it (and can build disjoint ranges — e.g. 1–5, then 7–10) rather
        // than replacing everything with just the new range.
        const selected = new Set(prev.selectedIndices);
        for (const i of createSelectionRange(anchorIndex, index)) {
          selected.add(i);
        }
        return {
          focusedIndex: index,
          selectionAnchorIndex: anchorIndex,
          selectedIndices: [...selected].sort((a, b) => a - b),
        };
      });
    },
    [createSelectionRange]
  );

  const handleRowClick = useCallback(
    (item: T, index: number, event: React.MouseEvent<HTMLDivElement>) => {
      focusFromKeyboardRef.current = false;
      if (event.shiftKey) {
        handleRangeSelectTo(index);
        return;
      }
      // Once a multi-selection is active, a plain click toggles the row's
      // selection instead of navigating — so clicking anywhere on a row (not
      // just its checkbox) can't accidentally yank you off the page mid-select.
      if (selectedIndices.length >= 1) {
        handleToggleSelect(index);
        return;
      }
      // Regular click opens the item; it focuses but does not select, so the
      // selection bar only appears for deliberate shift/checkbox selections.
      setSelectionState({
        focusedIndex: index,
        selectionAnchorIndex: index,
        selectedIndices: [],
      });
      onClickItem?.(item);
    },
    [handleRangeSelectTo, onClickItem, selectedIndices, handleToggleSelect]
  );

  const clearSelection = useCallback(() => {
    setSelectionState({
      focusedIndex: null,
      selectionAnchorIndex: null,
      selectedIndices: [],
    });
    onFocusItems?.([]);
  }, [onFocusItems]);

  const resolveScrollElement = useCallback(
    (): HTMLElement | null =>
      (useExternalScroll ? contentScrollRef?.current : listRef.current) ?? null,
    [useExternalScroll, contentScrollRef]
  );

  const {
    dragState,
    onDragStart: handleRowDragStart,
    onDragOver: handleRowDragOver,
    onDrop: handleRowDrop,
    onDragEnd: handleRowDragEnd,
  } = useResourceListDrag<T>({
    items: filteredAndSortedItems,
    selectedIndices,
    selectedIndexSet,
    canDropOnItem,
    onDropOnItem,
    renderDragImage,
    resolveScrollElement,
    dragImageHostRef: containerRef,
    onDropCompleted: clearSelection,
  });

  const handleToggleSelectAll = useCallback(() => {
    focusFromKeyboardRef.current = false;
    const count = filteredAndSortedItems.length;
    setSelectionState((prev) => {
      // Already all-selected → clear; otherwise select every visible row.
      if (count === 0 || prev.selectedIndices.length === count) {
        return {
          focusedIndex: prev.focusedIndex,
          selectionAnchorIndex: null,
          selectedIndices: [],
        };
      }
      return {
        focusedIndex: prev.focusedIndex ?? 0,
        selectionAnchorIndex: 0,
        selectedIndices: Array.from({ length: count }, (_, i) => i),
      };
    });
  }, [filteredAndSortedItems.length]);

  const handleSearchToggle = useCallback(() => {
    if (searchOpen) {
      setSearchQuery('');
      setSearchOpen(false);
    } else {
      setSearchOpen(true);
    }
  }, [searchOpen, setSearchQuery]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchQuery('');
        setSearchOpen(false);
      }
    },
    [setSearchQuery, setSearchOpen]
  );

  const handleSortSelect = useCallback(
    (key: string) => {
      const nextDir =
        activeSort === key ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
      if (onSortChange !== undefined) {
        onSortChange(key, nextDir);
      } else {
        setInternalActiveSort(key);
        setInternalSortDirection(nextDir);
      }
    },
    [activeSort, sortDirection, onSortChange]
  );

  useEffect(() => {
    const header = stickyHeaderRef.current;
    const container = containerRef.current;
    if (header === null || container === null) {
      return;
    }
    const publish = (): void => {
      container.style.setProperty(
        '--list-sticky-header-h',
        `${header.offsetHeight}px`
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const isLoading = status === 'loading';
  const isError = status === 'error';
  /**
   * The list has resolved and there is genuinely nothing in it.
   *
   * Deliberately not just `length === 0`. An empty list that is still loading, or
   * that is empty because the request failed, keeps its column headers and its
   * count — the skeleton and the retry stand in for rows, and dropping the
   * chrome under them makes a pending list look like an answered one. Only a
   * resolved-empty list sheds them.
   *
   * The count heading deliberately does *not* use this; see `nothingToCount`
   * below. The three sites were written as three near-copies of
   * `length === 0`, but only two of them wanted the same answer.
   */
  const isEmpty = filteredAndSortedItems.length === 0;
  const resolvedEmpty = !isLoading && !isError && isEmpty;
  /**
   * No number to print: the load has finished (or failed) and there are no rows.
   *
   * Unlike {@link resolvedEmpty} this is true on an error too. The headers and
   * the toolbar keep their chrome through a failed load because the retry stands
   * in for rows; a count can't, because a count is a fact and a load that failed
   * does not have one. "0 chats" over a retry button is a worse answer than no
   * answer.
   */
  const nothingToCount = !isLoading && isEmpty;
  // A query restored from the URL (`?q=…`) must show its input, or the list
  // arrives silently filtered with no visible cause and no way to clear it.
  const isSearchVisible = searchOpen || searchQuery.trim() !== '';
  // Prefer the caller-provided total (paginated lists only load a subset into
  // `items`); fall back to the loaded count when no total is supplied.
  const count = totalCount ?? items.length;
  const countLabel = `${count} ${noun}${count !== 1 ? 's' : ''}`;
  const baseSubtitle = subtitleOverride ?? countLabel;
  const listSubtitle = baseSubtitle;
  const hasSortOptions = sortOptions !== undefined && sortOptions.length > 0;
  const hasFilterOptions =
    filterOptions !== undefined && filterOptions.length > 0;
  const useCustomIcon = renderIcon !== undefined;
  const hasItemActions = itemActions !== undefined;
  // A selection is active as soon as one row is intentionally selected
  // (shift-click / checkbox / shift-arrow). Regular click opens without
  // selecting, so it doesn't trip this.
  const multiSelectActive = selectedIndices.length >= 1;
  const selectedItems = useMemo(
    () =>
      selectedIndices
        .map((index) => filteredAndSortedItems[index])
        .filter((item): item is T => item !== undefined),
    [selectedIndices, filteredAndSortedItems]
  );
  // In always-show mode the checkbox lives in its own persistent leading column,
  // so the icon column keeps rendering the type icon / hover actions regardless
  // of whether a selection is active.
  const showSelectColumn = alwaysShowSelection;
  const allSelected =
    filteredAndSortedItems.length > 0 &&
    selectedIndices.length === filteredAndSortedItems.length;
  const selectAllState: boolean | 'indeterminate' = allSelected
    ? true
    : selectedIndices.length > 0
    ? 'indeterminate'
    : false;

  // Rows render a trailing ⋯ column whenever the list asks for one and has
  // per-row actions; the header must reserve the same slot.
  const hasTrailingActionsColumn =
    (trailingActionsColumn ?? showSelectColumn) && hasItemActions;
  const colgroupEl = (
    <ResourceListColgroup
      columns={columns}
      hasTreeColumn={isTreeList}
      hasTrailingActionsColumn={hasTrailingActionsColumn}
    />
  );

  const isEmbeddedLayout = layout === 'embedded' || layout === 'panel';
  const isPanelLayout = layout === 'panel';
  const isCompactHeader = headerSize === 'compact';

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex flex-col',
        // The query container the column layout switches on, for the header row,
        // the rows' stacking, and every row renderer's metadata cells — all of
        // which flip together at `--container-list-cols` (see `styles.css`).
        //
        // It has to be here rather than on the list body: the column *header* is
        // a sibling of the body, and header and rows must answer to one width or
        // the header labels columns the rows aren't laying out.
        //
        // Safe despite the containment `container-type` implies: the virtualised
        // rows are absolutely positioned against the spacer below, which sets
        // its own `position: relative`, so their containing block is unchanged.
        '@container/list',
        // External scroll: grow to at least full height and let the ancestor
        // scroll container handle overflow. Internal scroll: stay bounded to the
        // parent height so the list body below can scroll within `overflow-auto`.
        useExternalScroll ? 'min-h-full' : 'h-full min-h-0',
        containerClassName ?? (isPanelLayout ? 'px-3 pt-2' : 'px-8')
      )}
    >
      <div
        ref={stickyHeaderRef}
        className={cn(
          'shrink-0',
          // Keep the top spacing *inside* the sticky header so it stays pinned
          // from the first pixel of scroll, instead of falling up through the
          // container's top padding (which made the header jump/wobble).
          // The mobile step is `pt-1`, not `pt-4`. This padding stacks on the
          // page frame's own top gutter, and under a 44px nav strip the two
          // together pushed the page's title about 30px down a viewport that
          // has roughly twelve rows in it — a band of empty canvas between the
          // hamburger and the only thing that says what page you are on. The
          // `md:` steps are the desktop rhythm and are unchanged.
          !isPanelLayout &&
            (isEmbeddedLayout
              ? 'pt-1 sm:pt-4'
              : isCompactHeader
              ? 'pt-1 sm:pt-4 md:pt-6'
              : 'pt-1 sm:pt-4 md:pt-10'),
          useExternalScroll &&
            // Solid (not translucent + backdrop-blur): backdrop-filter on a
            // sticky element causes repaint jumps in Chrome when you reverse
            // scroll direction.
            'sticky top-0 z-10 bg-background -mr-8 pr-8',
          // The header background must reach into the left margin to hide the
          // selection checkbox as rows scroll under it. (The ⋯ menu used to live
          // out here too and needed -ml-16; it now has a trailing column.)
          useExternalScroll && '-ml-8 pl-8'
        )}
      >
        <div
          className={cn(
            // `gap-2` on a phone: at `gap-4` the title and the toolbar were 16px
            // apart on a 335px content column, which is the difference between
            // the `+` button fitting and being pushed off the right edge.
            'flex items-center justify-between gap-2 sm:gap-4',
            isPanelLayout ? 'mb-2 pt-0' : isCompactHeader ? 'mb-3' : 'mb-4',
            !isPanelLayout &&
              (isEmbeddedLayout || isCompactHeader ? 'pt-0' : 'pt-4')
          )}
        >
          {isPanelLayout ? (
            <span className="text-xs text-muted-foreground tabular-nums">
              {countLabel}
            </span>
          ) : hideTitle ? (
            <ListCountHeading
              titleLoading={titleLoading}
              nothingToCount={nothingToCount}
              subtitle={listSubtitle}
            />
          ) : (
            // `min-w-0`: the title is the cell that gives. Without it a long
            // folder name held its full width and pushed the toolbar — search,
            // filter, `+` — off the right edge of a phone with no way to scroll
            // to it.
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              {titleLoading ? (
                <>
                  <Skeleton
                    className={cn(
                      RESOURCE_LIST_SKELETON_CLASS,
                      'h-9 w-48 rounded'
                    )}
                  />
                  {/* Still a sibling in this branch, and correctly so: the
                      skeleton is one line, so centring against it is centring
                      against the title it stands in for. */}
                  {titleAdornment !== undefined && titleAdornment !== null ? (
                    <span className="flex shrink-0 items-center">
                      {titleAdornment}
                    </span>
                  ) : null}
                </>
              ) : (
                <Text.Title
                  type="title"
                  className={cn(
                    'min-w-0 truncate text-slate-800 !font-bold !font-serif !leading-tight',
                    // 36px is a page heading on a desktop and a third of a
                    // phone's width; it steps down to the compact size below
                    // `sm` so the name and the controls beside it fit on one
                    // line.
                    isCompactHeader ? '!text-2xl' : '!text-2xl sm:!text-4xl'
                  )}
                  subtitle={listSubtitle}
                  // The count is a desktop line. Below `sm` it was a whole row
                  // of a viewport that has about twelve, and inlining it after
                  // the heading was worse — Automations' summary is a sentence,
                  // so it truncated to "49 workflows · 18 trig…", which is
                  // clutter that loses the one part worth reading
                  // ("1 failing"). The list itself says how many things are in
                  // it.
                  subtitleClassName="hidden sm:block"
                  // Handed to the heading rather than dropped beside it. As a
                  // sibling of the whole `Text.Title` the star was centred
                  // against a two-line block — the name *and* the "N items"
                  // subtitle under it — so on `sm` and up, where that subtitle
                  // is visible, it hung visibly below the name it belongs to.
                  //
                  // The heading owns that stack and so is the only thing that
                  // can align a mark to the title line without a second copy of
                  // the subtitle's styling. It keeps the star outside the
                  // truncating span either way, which is what stops an ellipsis
                  // eating it on a long folder name.
                  adornment={titleAdornment}
                >
                  {title}
                </Text.Title>
              )}
            </div>
          )}

          <ResourceListToolbar<T>
            hasSortOptions={hasSortOptions}
            sortOptions={sortOptions}
            activeSort={activeSort}
            sortDirection={sortDirection}
            onSortSelect={handleSortSelect}
            searchOpen={isSearchVisible}
            searchQuery={searchQuery}
            searchPlaceholder={searchPlaceholder}
            onSearchToggle={handleSearchToggle}
            onSearchQueryChange={setSearchQuery}
            onSearchKeyDown={handleSearchKeyDown}
            searchInputRef={searchInputRef}
            hasFilterOptions={hasFilterOptions}
            filterOptions={filterOptions}
            primaryAction={primaryAction}
            toolbarExtras={toolbarExtras}
            // An empty list has nothing to sort, search or filter. Kept when a
            // search or filter is what emptied it, so the controls that caused
            // the empty result are still there to undo it.
            hideNarrowingControls={
              resolvedEmpty && searchQuery.trim() === '' && !filtersActive
            }
          />
        </div>

        {headerExtras !== undefined ? (
          <div ref={headerExtrasRef} onKeyDown={handleHeaderExtrasKeyDown}>
            {headerExtras}
          </div>
        ) : null}

        {/* Grid mode has no columns to head — and neither does an empty list.
            A header row labelling Name / Created by / Shared with above an
            empty state is a table of contents for a table that isn't there; it
            also framed the "no chats yet" message as a failed query rather than
            an empty one. Kept during loading and error, where the skeleton and
            the retry both stand in for rows and the header stops the layout
            jumping when they arrive. */}
        {contentOverride === undefined &&
        !isPanelLayout &&
        !isGridMode &&
        !resolvedEmpty ? (
          <ResourceListColumnHeaders
            columns={columns}
            colgroup={colgroupEl}
            compact={isEmbeddedLayout}
            hasTreeColumn={isTreeList}
            hasTrailingActionsColumn={hasTrailingActionsColumn}
            selectAll={
              showSelectColumn
                ? {
                    state: selectAllState,
                    onToggle: handleToggleSelectAll,
                  }
                : undefined
            }
            sort={
              hasSortOptions
                ? {
                    activeSort,
                    sortDirection,
                    onSortSelect: handleSortSelect,
                  }
                : undefined
            }
          />
        ) : null}
      </div>

      {contentOverride !== undefined ? (
        contentOverride
      ) : isError ? (
        <ResourceListErrorState
          errorText={errorText}
          onRetry={onRetry}
          compact={isPanelLayout}
        />
      ) : isLoading ? (
        isGridMode ? (
          <ResourceGridLoadingSkeleton
            columns={gridColumns}
            cardHeight={GRID_CARD_HEIGHT_PX}
            gap={GRID_GAP_PX}
          />
        ) : (
          <ResourceListLoadingSkeleton
            colgroup={colgroupEl}
            columnCount={columns.length}
          />
        )
      ) : filteredAndSortedItems.length === 0 ? (
        <ResourceListEmptyState
          emptyIcon={emptyIcon}
          emptyText={emptyText}
          noMatchText={noMatchText}
          searchQueryTrimmed={searchQuery.trim()}
          primaryAction={primaryAction}
          onClearFilters={onClearFilters}
          filtersActive={filtersActive}
          compact={isPanelLayout}
          smallIcon={isEmbeddedLayout}
        />
      ) : (
        <div
          ref={listRef}
          // `grid`, not `listbox`: rows carry interactive descendants (selection
          // checkbox, ⋯ menu) that the option role forbids. `treegrid` when rows
          // can expand, so depth and expansion are announced.
          role={isTreeList && !isGridMode ? 'treegrid' : 'grid'}
          aria-multiselectable={
            selectionActions !== undefined ? true : undefined
          }
          aria-rowcount={count}
          aria-busy={isRefetching ? true : undefined}
          tabIndex={0}
          aria-label={title}
          aria-activedescendant={
            focusedIndex !== null &&
            filteredAndSortedItems[focusedIndex] !== undefined
              ? `resource-list-option-${focusedIndex}`
              : undefined
          }
          onKeyDown={handleListKeyDown}
          onFocus={handleListFocus}
          onBlur={() => setListHasFocus(false)}
          onClick={(e) => {
            (e.currentTarget as HTMLDivElement).focus();
          }}
          className={cn(
            'flex-1 min-h-0 -mr-4 pr-4',
            !useExternalScroll && 'overflow-y-auto',
            'outline-none',
            // Grid has no column-header band under the filters, so it needs its
            // own breathing room to match the separation list mode gets for free.
            isGridMode && 'pt-2',
            // Rows already on screen are from the previous query while a new one
            // is in flight; dim them so the swap isn't a silent surprise.
            isRefetching && 'opacity-50 transition-opacity pointer-events-none'
          )}
        >
          <div
            style={{ height: `${totalHeight}px`, position: 'relative' }}
            className="w-full"
          >
            {isGridMode && renderCard !== undefined
              ? virtualItems.map((virtualRow) => {
                  // Each virtual row holds `gridColumns` cards.
                  const start = virtualRow.index * gridColumns;
                  const rowItems = filteredAndSortedItems.slice(
                    start,
                    start + gridColumns
                  );
                  return (
                    <div
                      key={virtualRow.index}
                      role="row"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                        height: `${GRID_CARD_HEIGHT_PX}px`,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                        gap: `${GRID_GAP_PX}px`,
                        marginBottom: `${GRID_GAP_PX}px`,
                      }}
                    >
                      {rowItems.map((cardItem, offset) => {
                        const index = start + offset;
                        return (
                          <VirtualGridCard
                            key={keyFn(cardItem)}
                            item={cardItem}
                            index={index}
                            optionId={`resource-list-option-${index}`}
                            renderCard={renderCard}
                            actions={
                              itemActions !== undefined
                                ? itemActions(cardItem)
                                : []
                            }
                            onOpenActionMenu={onOpenActionMenu}
                            isSelected={selectedIndexSet.has(index)}
                            isFocused={listHasFocus && focusedIndex === index}
                            multiSelectActive={multiSelectActive}
                            isDraggable={
                              dndEnabled && (canDragItem?.(cardItem) ?? true)
                            }
                            isDragging={
                              dragState?.draggingIndices.includes(index) ??
                              false
                            }
                            isDropTarget={dragState?.dropTargetIndex === index}
                            onDragStartRow={
                              dndEnabled ? handleRowDragStart : undefined
                            }
                            onDragOverRow={
                              dndEnabled ? handleRowDragOver : undefined
                            }
                            onDropRow={dndEnabled ? handleRowDrop : undefined}
                            onDragEndRow={
                              dndEnabled ? handleRowDragEnd : undefined
                            }
                            onRowClick={handleRowClick}
                            onToggleSelect={handleToggleSelect}
                            onShiftSelect={handleRangeSelectTo}
                            onMouseEnterRow={(i) => {
                              focusFromKeyboardRef.current = false;
                              setSelectionState((prev) => ({
                                ...prev,
                                focusedIndex: i,
                              }));
                            }}
                            onClickItem={onClickItem}
                          />
                        );
                      })}
                    </div>
                  );
                })
              : virtualItems.map((virtualRow) => {
                  const index = virtualRow.index;
                  const item = filteredAndSortedItems[index];
                  if (item === undefined) {
                    return null;
                  }
                  const isSelected = selectedIndexSet.has(index);
                  // Any eligible row is draggable (Finder / Drive behavior).
                  // Restricting drags to already-selected rows meant a user's first
                  // instinct — grab a row and drag it onto a folder — did nothing,
                  // so the feature read as absent. Dragging a row that isn't part of
                  // the selection carries just that row; see `handleRowDragStart`.
                  const isDraggable =
                    dndEnabled && (canDragItem?.(item) ?? true);
                  const isDragging =
                    dragState?.draggingIndices.includes(index) ?? false;
                  const isDropTarget = dragState?.dropTargetIndex === index;
                  return (
                    <VirtualListItem
                      key={keyFn(item)}
                      item={item}
                      index={index}
                      virtualStartY={virtualRow.start}
                      optionId={`resource-list-option-${index}`}
                      compactRows={isEmbeddedLayout}
                      renderItem={renderItem}
                      icon={Icon}
                      renderIcon={renderIcon}
                      useCustomIcon={useCustomIcon}
                      hasItemActions={hasItemActions}
                      itemActions={itemActions}
                      onOpenActionMenu={onOpenActionMenu}
                      isSelected={isSelected}
                      isFocused={listHasFocus && focusedIndex === index}
                      multiSelectActive={multiSelectActive}
                      showSelectColumn={showSelectColumn}
                      trailingActions={hasTrailingActionsColumn}
                      isDraggable={isDraggable}
                      isDragging={isDragging}
                      isDropTarget={isDropTarget}
                      onDragStartRow={
                        dndEnabled ? handleRowDragStart : undefined
                      }
                      onDragOverRow={dndEnabled ? handleRowDragOver : undefined}
                      onDropRow={dndEnabled ? handleRowDrop : undefined}
                      onDragEndRow={dndEnabled ? handleRowDragEnd : undefined}
                      onRowClick={handleRowClick}
                      onToggleSelect={handleToggleSelect}
                      onShiftSelect={handleRangeSelectTo}
                      onMouseEnterRow={(i) => {
                        // Hover moves the cursor but must never scroll the list.
                        focusFromKeyboardRef.current = false;
                        setSelectionState((prev) => ({
                          ...prev,
                          focusedIndex: i,
                        }));
                      }}
                      rowRef={measureRowElement}
                      onClickItem={onClickItem}
                      href={itemHref?.(item)}
                      chrome={itemChrome?.(item) ?? 'default'}
                      treeMeta={itemTreeMeta?.(item)}
                      onToggleExpand={onToggleExpand}
                    />
                  );
                })}
          </div>
          {hasMore ? (
            <div
              ref={sentinelRef}
              className="h-4 w-full shrink-0"
              aria-hidden
            />
          ) : null}
          {/* Fetching the next page was previously silent: you hit the bottom of
              a long library and nothing said work was happening. */}
          {isLoadingMore ? (
            <div
              role="status"
              aria-live="polite"
              className="flex w-full shrink-0 items-center justify-center gap-2 py-4 text-xs text-muted-foreground"
            >
              <Loader2
                className="h-3.5 w-3.5 animate-spin"
                aria-hidden="true"
              />
              <span>Loading more…</span>
            </div>
          ) : null}
          {isPanelLayout ? null : <div className="pb-8" />}
        </div>
      )}
      {selectionActions !== undefined && selectedItems.length >= 1 ? (
        <SelectionActionBar
          count={selectedItems.length}
          actions={selectionActions(selectedItems)}
          onClear={clearSelection}
        />
      ) : null}
    </div>
  );
}
